// J6 report · J7 friction (shown on Sell, via the same gate) · J8 the case · J9 the decision · triage · rules · vouch.

import type { ActorId, Claim } from '@model/index.ts';
import { html } from '../html.ts';
import { items, t, tn } from '../i18n.ts';
import type { Key } from '../i18n.ts';
import { flash, go, local, on, onForm, refuse } from '../core.ts';
import type { Screen } from '../core.ts';
import { DAY, world as w } from '../world.ts';
import {
  canSee,
  caseAudience,
  claim,
  codeOf,
  completedCount,
  decisionOn,
  distinctReporters,
  embedded,
  isMember,
  joinInfo,
  messagesWith,
  num,
  ofPred,
  flagsFor,
  openAppeals,
  openCases,
  proofFor,
  repliesTo,
  openReportBy,
  param,
  proofAudience,
  promoteBody,
  caseReasonKey,
  reasonKey,
  saidAbout,
  stewards,
  str,
  triage,
} from '../queries.ts';
import { explain, offerGate, reason } from '../explain.ts';
import { back, linkButton, personHref, tradesLine, whyCard } from '../ui.ts';
import { fmtDate, fmtDateTime } from '../format.ts';
import { confirmDialog } from '../dialog.ts';

const REASONS = ['advance', 'noshow', 'notAsDescribed', 'abuse', 'other'];

function messagePicker(other: ActorId) {
  const me = w.me.id;
  const msgs = messagesWith(w, me, other);
  if (!msgs.length) return html``;
  return html`<fieldset class="choices proof">
    <legend>${t('report.attach')}</legend>
    <p class="muted">${t('report.attachHelp', { name: w.name(other) })}</p>
    ${msgs.map(
      (m) => html`<label class="choice choice-msg">
        <input type="checkbox" name="proof" value="${m.id}" ${m.author === other ? 'checked' : ''} />
        <span><b>${m.author === me ? t('common.you') : w.name(m.author)}</b> · ${fmtDateTime(m.issuedAt)}<br />${str(m, 'text')}</span>
      </label>`,
    )}
  </fieldset>`;
}

export function reportScreen(key: string): Screen {
  const p = w.byKey.get(key);
  if (!p) return { title: '', tab: 'nearby', body: html`${back('#/')}<p>Not found.</p>` };
  const me = w.me;
  const name = p.name[w.language];
  const open = openReportBy(w, me.id, p.id);
  const title = t('report.title', { name });
  if (open) {
    return {
      title,
      tab: 'nearby',
      body: html`
        ${back(personHref(p.id))}
        <h1 tabindex="-1">${title}</h1>
        <p class="lead">${t('report.already', { name, date: fmtDate(open.issuedAt) })}</p>
        <form class="stack" data-form="promote" data-report="${open.id}" data-subject="${p.id}">
          ${messagePicker(p.id)}
          <button class="btn" type="submit">${t('report.addMore')}</button>
        </form>
      `,
    };
  }
  // Ask the gate first: a person who may not report sees why before writing anything.
  const gate = w.would(me.key, { predicate: 'report', subject: p.id, audience: caseAudience(w, me.id), body: { reason: 'other' } });
  if (!gate.admitted) {
    const why = explain(w, 'report', gate, me.id);
    w.noteCheck(me.id, 'report', gate, why.summary);
    return {
      title,
      tab: 'nearby',
      body: html`${back(personHref(p.id))}<h1 tabindex="-1">${title}</h1><p class="danger">${t('report.danger')}</p>${whyCard(why, t('why.report'))}`,
    };
  }
  return {
    title,
    tab: 'nearby',
    body: html`
      ${back(personHref(p.id))}
      <h1 tabindex="-1">${title}</h1>
      <p class="danger">${t('report.danger')}</p>
      <form class="stack" data-form="report" data-subject="${p.id}">
        <fieldset class="choices">
          <legend>${t('report.what')}</legend>
          ${REASONS.map(
            (r) => html`<label class="choice"><input type="radio" name="reason" value="${r}" required /><span>${t(reasonKey(r) as Key, { g: p.g })}</span></label>`,
          )}
        </fieldset>
        <label class="field"><span>${t('report.details')} <small>(${t('common.optional')})</small></span>
          <textarea name="details" rows="3" maxlength="500"></textarea></label>
        ${messagePicker(p.id)}
        <p class="muted">${t('report.ai')}</p>
        <button class="btn" type="submit">${t('report.send')}</button>
      </form>
    `,
  };
}

async function promoteAll(reportId: string, ids: string[]): Promise<boolean> {
  const me = w.me;
  let ok = true;
  for (const id of ids) {
    const msg = w.device.find((m) => m.id === id);
    if (!msg) continue;
    const { d } = await w.act(me.key, { predicate: 'promote', refs: [reportId], audience: proofAudience(w, me.id), body: promoteBody(msg) as never });
    if (!d.admitted) {
      refuse('promote', t('why.promote'), d);
      ok = false;
    }
  }
  return ok;
}

onForm('report', async (fd, form) => {
  if (!form.reportValidity()) return;
  const subject = form.dataset.subject!;
  const me = w.me;
  const details = String(fd.get('details') ?? '').trim();
  const { claim: rep, d } = await w.act(me.key, {
    predicate: 'report',
    subject,
    audience: caseAudience(w, me.id),
    body: { reason: String(fd.get('reason')), details },
  });
  if (!d.admitted) {
    refuse('report', t('why.report'), d);
    return go(personHref(subject));
  }
  await promoteAll(rep.id, fd.getAll('proof').map(String));
  flash(t('report.sent', { name: w.name(subject) }));
  go('#/me');
});

onForm('promote', async (fd, form) => {
  const ids = fd.getAll('proof').map(String);
  if (await promoteAll(form.dataset.report!, ids)) flash(t('report.added'));
  go('#/me');
});

// J8: the steward's case view. Every open report about one person, the proof, the AI's advice, the record.
export function casesScreen(): Screen {
  const cases = openCases(w, w.me.id);
  const appeals = openAppeals(w, w.me.id);
  return {
    title: t('cases.title'),
    tab: 'cases',
    body: html`
      <h1 tabindex="-1">${t('cases.title')}</h1>
      ${appeals.length
        ? html`<ul class="items" data-testid="appeals">
            ${appeals.map((a) => {
              const d = claim(w, a.refs[0])!;
              return html`<li class="item"><a class="item-link" href="#/appeal/${a.id}">
                <span class="item-title">${t('appeals.row', { name: w.name(a.author) })}</span>
                <span class="item-meta">${t('appeals.meta', { steward: w.name(d.author), date: fmtDate(d.issuedAt) })}</span>
              </a></li>`;
            })}
          </ul>`
        : ''}
      ${cases.length || appeals.length ? '' : html`<p class="muted">${t('cases.empty')}</p>`}
      ${cases.length
        ? html`<ul class="items">
            ${cases.map((c) => {
              const flag = c.flags.at(-1);
              return html`<li class="item"><a class="item-link" href="#/case/${w.byId.get(c.subject)?.key}">
                <span class="item-title">${w.name(c.subject)}</span>
                <span class="item-price">${tn('cases.reports', c.reports.length)}</span>
                <span class="item-meta">${flag ? t('cases.ai', { level: t(`risk.${str(flag, 'level')}` as Key) }) : t('cases.noAi')}</span>
              </a></li>`;
            })}
          </ul>`
        : ''}
    `,
  };
}

// What a steward reads for any case: the reports, the proof, the AI's advice. Shared by cases and appeals.
function reportsSection(reports: Claim[], g: 'm' | 'f') {
  return html`<ul class="said">
    ${reports.map(
      (r) => html`<li>
        <p><b>${t(caseReasonKey(str(r, 'reason')) as Key, { g })}</b></p>
        <p class="muted">${w.name(r.author)} · ${fmtDateTime(r.issuedAt)}</p>
        ${str(r, 'details') ? html`<p>“${str(r, 'details')}”</p>` : ''}
      </li>`,
    )}
  </ul>`;
}
function proofSection(proof: Claim[]) {
  return proof.length
    ? html`<ul class="said proof-list">
        ${proof.map((pr) => {
          const m = embedded(pr);
          if (!m) return '';
          const to = Array.isArray(m.audience) ? m.audience.map((a) => w.name(a)).join(', ') : '';
          return html`<li>
            <p class="msg">“${str(m, 'text')}”</p>
            <p class="sig">${t('case.sig', { from: w.name(m.author), to, date: fmtDateTime(m.issuedAt) })}</p>
          </li>`;
        })}
      </ul>`
    : html`<p class="muted">${t('case.noProof')}</p>`;
}
function adviceSection(flag: Claim | undefined) {
  return flag
    ? html`<div class="advice" data-testid="ai-note"><p><b>${t(`riskTitle.${str(flag, 'level')}` as Key)}</b></p><p>${str(flag, 'note')}</p></div>`
    : html`<p class="muted">${t('case.noAi')}</p>`;
}

// The case as a timeline: every claim this steward can see, oldest first, each in one plain line.
function timeline(claims: (Claim | undefined)[]) {
  const seen = new Set<string>();
  const list = claims
    .filter((c): c is Claim => !!c && !seen.has(c.id) && (seen.add(c.id), true))
    .sort((a, b) => a.issuedAt - b.issuedAt);
  if (!list.length) return html``;
  const line = (c: Claim): string => {
    const name = w.name(c.author);
    if (c.predicate === 'decide') {
      const outcome = str(c, 'outcome') === 'suspend-offers' ? t('outcome.pause', { date: fmtDate(num(c, 'until')) }) : t('outcome.none');
      return t('tl.decide', { name, outcome });
    }
    const key = `tl.${c.predicate}`;
    return ['report', 'promote', 'flag', 'respond', 'appeal'].includes(c.predicate) ? t(key as Key, { name }) : c.predicate;
  };
  return html`<h2>${t('tl.title')}</h2>
    <ol class="timeline">
      ${list.map(
        (c) => html`<li class="tl-${c.predicate}"><time datetime="${new Date(c.issuedAt).toISOString()}">${fmtDateTime(c.issuedAt)}</time><span>${line(c)}</span></li>`,
      )}
    </ol>`;
}

// The draft a steward would sign to close every open report about this person (kernel R6: decide.each-report).
const caseDraft = (subject: string, reports: Claim[]) => ({
  predicate: 'decide',
  subject,
  refs: reports.map((r) => r.id),
  body: { outcome: 'no-action', until: 0, note: '-' },
});

export function caseScreen(key: string): Screen {
  const p = w.byKey.get(key);
  const me = w.me.id;
  const c = p && openCases(w, me).find((x) => x.subject === p.id);
  if (!p || !c) return { title: '', tab: 'cases', body: html`${back('#/cases')}<p class="muted">${t('cases.empty')}</p>` };
  const name = p.name[w.language];
  const gate = offerGate(w, w.would(p.key, { predicate: 'offer', body: {} }, me));
  const j = joinInfo(w, p.id);
  const said = saidAbout(w, p.id);
  const past = ofPred(w, 'decide').filter((d) => d.subject === p.id);
  // Ask the kernel whether this steward may decide (it refuses a steward who filed one of the reports, R7).
  const may = w.would(w.seat, caseDraft(p.id, c.reports));
  const why = may.admitted ? null : explain(w, 'decide', may, me);
  if (why) w.noteCheck(me, 'decide', may, why.summary);
  return {
    title: t('case.title', { name }),
    tab: 'cases',
    body: html`
      ${back('#/cases')}
      <h1 tabindex="-1">${t('case.title', { name })}</h1>
      <h2>${t('case.now')}</h2>
      <p class="state" data-testid="case-now">${gate.gate === 'friction'
        ? t('case.nowFriction', { name, g: p.g, items: items(gate.limit), reporters: gate.reporters ?? param(w, 'frictionReporters') })
        : t('case.nowNone', { name })}</p>
      <h2>${t('case.reported')}</h2>
      ${reportsSection(c.reports, p.g)}
      <h2>${t('case.proof')}</h2>
      ${proofSection(c.proof)}
      <h2>${t('case.ai')}</h2>
      ${adviceSection(c.flags.at(-1))}
      ${timeline([...c.reports, ...c.proof, ...c.flags, ...past.filter((d) => canSee(w, me, d))])}
      <h2>${t('case.record', { name })}</h2>
      <ul class="facts">
        ${j ? html`<li>${j.voucher ? t('standing.joinedVouch', { date: fmtDate(j.at), voucher: w.name(j.voucher), g: p.g }) : t('standing.joinedPhone', { date: fmtDate(j.at), g: p.g })}</li>` : ''}
        <li>${tradesLine(p.id)}</li>
        <li>${said.length ? `${t('standing.said')}: ${said.length}` : t('standing.nothingSaid')}</li>
        ${past.map((d) => html`<li>${fmtDate(d.issuedAt)}: ${str(d, 'outcome') === 'suspend-offers' ? t('outcome.pause', { date: fmtDate(num(d, 'until')) }) : t('outcome.none')}</li>`)}
      </ul>
      ${why ? whyCard(why, t('why.decide')) : linkButton(t('case.decide'), `#/decide/${p.key}`)}
    `,
  };
}

// J11: an appeal, read by a steward. The kernel decides who may answer it (decide.other-steward).
export function appealCaseScreen(appealId: string): Screen {
  const a = claim(w, appealId);
  const me = w.me.id;
  const d = a && claim(w, a.refs[0]);
  if (!a || !d || !canSee(w, me, a)) return { title: '', tab: 'cases', body: html`${back('#/cases')}<p class="muted">${t('cases.empty')}</p>` };
  const p = w.byId.get(a.author)!;
  const name = p.name[w.language];
  const reports = d.refs.map((id) => claim(w, id)).filter((c): c is Claim => !!c && c.predicate === 'report' && canSee(w, me, c));
  const proof = reports.flatMap((r) => proofFor(w, r.id, me));
  const flags = reports.flatMap((r) => flagsFor(w, r.id, me));
  const reply = repliesTo(w, d.id, me).find((r) => r.author === p.id);
  const outcome = str(d, 'outcome') === 'suspend-offers' ? t('outcome.pause', { date: fmtDate(num(d, 'until')) }) : t('outcome.none');
  const may = w.would(w.seat, appealDraft(a, d, 'suspend-offers', num(d, 'until')));
  const why = may.admitted ? null : explain(w, 'decide', may, me);
  if (why) w.noteCheck(me, 'decide', may, why.summary);
  return {
    title: t('appealCase.title', { name }),
    tab: 'cases',
    body: html`
      ${back('#/cases')}
      <h1 tabindex="-1">${t('appealCase.title', { name })}</h1>
      <h2>${t('appealCase.appeal', { name })}</h2>
      <blockquote class="side-them" data-testid="appeal-text">${str(a, 'text')}</blockquote>
      ${reply ? html`<h2>${t('appealCase.reply')}</h2><blockquote class="side-them">${str(reply, 'text')}</blockquote>` : ''}
      <h2>${t('appealCase.decision')}</h2>
      <p class="muted">${t('appealCase.by', { steward: w.name(d.author), date: fmtDate(d.issuedAt), outcome })}</p>
      ${str(d, 'note') ? html`<blockquote class="side-steward">${str(d, 'note')}</blockquote>` : ''}
      <h2>${t('appealCase.original')}</h2>
      ${reportsSection(reports, p.g)}
      <h2>${t('case.proof')}</h2>
      ${proofSection(proof)}
      <h2>${t('case.ai')}</h2>
      ${adviceSection(flags.at(-1))}
      ${timeline([...reports, ...proof, ...flags, d, reply, a])}
      ${why ? whyCard(why, t('why.decide')) : linkButton(t('appealCase.decide'), `#/decide-appeal/${a.id}`)}
    `,
  };
}

// refs = [appeal, the decision appealed]: the shape the decide.appeal branch checks.
const appealDraft = (a: Claim, d: Claim, outcome: string, until: number) => ({
  predicate: 'decide',
  subject: a.author,
  refs: [a.id, d.id],
  audience: outcome === 'suspend-offers' ? ('scope' as const) : [...new Set([a.author, ...stewards(w), ...triage(w)])],
  body: { outcome, until, note: '-' },
});

export function decideAppealScreen(appealId: string): Screen {
  const a = claim(w, appealId);
  const d = a && claim(w, a.refs[0]);
  if (!a || !d) return { title: '', tab: 'cases', body: html`${back('#/cases')}<p class="muted">${t('cases.empty')}</p>` };
  const p = w.byId.get(a.author)!;
  const name = p.name[w.language];
  const until = num(d, 'until');
  const shorter = d.issuedAt + 3 * DAY;
  return {
    title: t('decide.title', { name }),
    tab: 'cases',
    body: html`
      ${back(`#/appeal/${a.id}`)}
      <h1 tabindex="-1">${t('decide.title', { name })}</h1>
      <form class="stack" data-form="decide-appeal" data-id="${a.id}">
        <fieldset class="choices">
          <legend>${t('decide.what')}</legend>
          <label class="choice"><input type="radio" name="outcome" value="keep" required /><span>${t('appealDecide.keep', { date: fmtDate(until) })}</span></label>
          ${shorter > w.now() && shorter < until
            ? html`<label class="choice"><input type="radio" name="outcome" value="shorten" /><span>${t('appealDecide.shorten', { date: fmtDate(shorter) })}</span></label>`
            : ''}
          <label class="choice"><input type="radio" name="outcome" value="lift" /><span>${t('appealDecide.lift')}</span></label>
        </fieldset>
        <label class="field"><span>${t('decide.reason', { name, g: p.g })}</span>
          <textarea name="note" rows="4" required maxlength="500"></textarea></label>
        <p class="note">${t('appealDecide.note')}</p>
        <button class="btn" type="submit">${t('decide.confirm')}</button>
      </form>
    `,
  };
}

onForm('decide-appeal', async (fd, form) => {
  if (!form.reportValidity()) return;
  const a = claim(w, form.dataset.id!)!;
  const d = claim(w, a.refs[0])!;
  const choice = String(fd.get('outcome'));
  const ok = await confirmDialog({ title: t('decide.sure', { name: w.name(a.author) }), body: t('appealDecide.note'), yes: t('decide.confirm') });
  if (!ok) return;
  const outcome = choice === 'lift' ? 'no-action' : 'suspend-offers';
  const until = choice === 'keep' ? num(d, 'until') : choice === 'shorten' ? d.issuedAt + 3 * DAY : w.now();
  const draft = appealDraft(a, d, outcome, until);
  const res = await w.act(w.seat, { ...draft, body: { outcome, until, note: String(fd.get('note')).trim() } });
  if (!res.d.admitted) {
    refuse('decide', t('why.decide'), res.d);
    return go(`#/appeal/${a.id}`);
  }
  flash(t('appealDecide.done', { name: w.name(a.author), g: w.gender(a.author) }));
  go('#/cases');
});

// J9: the decision. A human picks an outcome and writes the reason the person will read.
export function decideScreen(key: string): Screen {
  const p = w.byKey.get(key);
  const c = p && openCases(w, w.me.id).find((x) => x.subject === p.id);
  if (!p || !c) return { title: '', tab: 'cases', body: html`${back('#/cases')}<p class="muted">${t('cases.empty')}</p>` };
  const name = p.name[w.language];
  return {
    title: t('decide.title', { name }),
    tab: 'cases',
    body: html`
      ${back(`#/case/${key}`)}
      <h1 tabindex="-1">${t('decide.title', { name })}</h1>
      <form class="stack" data-form="decide" data-subject="${p.id}">
        <fieldset class="choices">
          <legend>${t('decide.what')}</legend>
          <label class="choice"><input type="radio" name="outcome" value="none" required /><span>${t('decide.none')}</span></label>
          ${[3, 7, 30].map(
            (days) => html`<label class="choice"><input type="radio" name="outcome" value="pause-${days}" /><span>${t('decide.pause', { days })}</span></label>`,
          )}
        </fieldset>
        <label class="field"><span>${t('decide.reason', { name, g: p.g })}</span>
          <textarea name="note" rows="4" required maxlength="500"></textarea></label>
        <p class="muted">${t('decide.public')}</p>
        <p class="note">${t('decide.final')}</p>
        <button class="btn" type="submit">${t('decide.confirm')}</button>
      </form>
    `,
  };
}

onForm('decide', async (fd, form) => {
  if (!form.reportValidity()) return;
  const subject = form.dataset.subject!;
  const c = openCases(w, w.me.id).find((x) => x.subject === subject);
  if (!c) return go('#/cases');
  const outcome = String(fd.get('outcome'));
  const note = String(fd.get('note')).trim();
  const ok = await confirmDialog({ title: t('decide.sure', { name: w.name(subject) }), body: t('decide.final'), yes: t('decide.confirm') });
  if (!ok) return;
  const pause = outcome.startsWith('pause-');
  const until = pause ? w.now() + Number(outcome.slice(6)) * DAY : w.now();
  const reporters = c.reports.map((r) => r.author);
  const { d } = await w.act(w.seat, {
    predicate: 'decide',
    subject,
    // ref0 is the report the rule checks; the rest close the other open reports in the same case (MODEL-REQUESTS R6).
    refs: c.reports.map((r) => r.id),
    audience: pause ? 'scope' : [...new Set([subject, ...reporters, ...stewards(w), ...triage(w)])],
    body: { outcome: pause ? 'suspend-offers' : 'no-action', until, note },
  });
  if (!d.admitted) {
    refuse('decide', t('why.decide'), d);
    return go(`#/case/${w.byId.get(subject)?.key}`);
  }
  flash(t('decide.done', { name: w.name(subject) }));
  go('#/cases');
});

// The AI seat. Deterministic triage from the report text and public records only.
function triageNote(r: Claim): { risk: number; level: string; note: string } {
  const subject = r.subject!;
  const reason = str(r, 'reason');
  const open = ofPred(w, 'report').filter((x) => x.subject === subject && !decisionOn(w, x.id));
  const reporters = distinctReporters(open);
  const trades = completedCount(w, subject);
  let risk = { advance: 0.6, abuse: 0.6, noshow: 0.35, notAsDescribed: 0.35 }[reason] ?? 0.25;
  const facts: string[] = [];
  if (reason === 'advance') facts.push('Asked for payment before showing the item, a common scam pattern.');
  if (reporters >= 2) {
    risk += 0.2;
    facts.push(`${reporters} different neighbours reported the same person.`);
  }
  if (trades === 0) {
    risk += 0.1;
    facts.push('No completed trades here yet.');
  }
  risk = Math.min(0.95, Math.round(risk * 100) / 100);
  const level = risk >= 0.7 ? 'high' : risk >= 0.45 ? 'medium' : 'low';
  return { risk, level, note: `${facts.join(' ')} Suggest a steward looks at this ${level === 'high' ? 'first' : 'when possible'}.` };
}

export function triageScreen(): Screen {
  const me = w.me.id;
  const reports = w.scope.visible(me).filter((c) => c.predicate === 'report' && !decisionOn(w, c.id));
  const waiting = reports.filter((r) => !ofPred(w, 'flag').some((f) => f.refs[0] === r.id && f.author === me));
  return {
    title: t('triage.title'),
    tab: null,
    body: html`
      <h1 tabindex="-1">${t('triage.title')}</h1>
      <p class="lead">${t('triage.body')}</p>
      ${waiting.length
        ? html`<ul class="said">${waiting.map(
            (r) => html`<li><p><b>${w.name(r.subject)}</b>: ${t(caseReasonKey(str(r, 'reason')) as Key, { g: w.gender(r.subject) })}</p>
              <p class="muted">${fmtDateTime(r.issuedAt)}${str(r, 'details') ? ` · “${str(r, 'details')}”` : ''}</p></li>`,
          )}</ul>
          <button class="btn" type="button" data-act="triage">${t('triage.run')}</button>`
        : html`<p class="muted">${t('triage.none')}</p>`}
      ${reports.length
        ? html`<p class="muted try">${t('triage.tryHelp')}</p>
          <button class="quiet" type="button" data-act="ai-decide" data-id="${reports[0]!.id}">${t('triage.try')}</button>`
        : ''}
      <p><a href="#/rules">${t('me.rules')}</a></p>
    `,
  };
}

on('triage', async () => {
  const me = w.me.id;
  const reports = w.scope.visible(me).filter((c) => c.predicate === 'report' && !decisionOn(w, c.id));
  for (const r of reports) {
    if (ofPred(w, 'flag').some((f) => f.refs[0] === r.id && f.author === me)) continue;
    const note = triageNote(r);
    await w.act(w.seat, { predicate: 'flag', refs: [r.id], audience: [...new Set([...stewards(w), ...triage(w)])], body: note });
  }
  go('#/triage');
});

on('ai-decide', async (el) => {
  const r = claim(w, el.dataset.id!)!;
  const { d } = await w.act(w.seat, {
    predicate: 'decide',
    subject: r.subject,
    refs: [r.id],
    audience: 'scope',
    body: { outcome: 'suspend-offers', until: w.now() + 7 * DAY, note: 'Automatic suspension.' },
  });
  if (!d.admitted) refuse('decide', t('why.decide'), d);
  go('#/triage');
});

// The rules, read from the live policy claim. The community seat can amend the dials.
export function rulesScreen(): Screen {
  const community = w.me.role === 'community';
  const names = stewards(w)
    .map((s) => w.name(s))
    .join(', ');
  const dial = (k: string, label: Key) =>
    html`<label class="field field-inline"><span>${t(label)}</span><input name="${k}" type="number" min="1" max="50" required value="${param(w, k)}" /></label>`;
  return {
    title: t('rules.title'),
    tab: community ? null : 'me',
    body: html`
      ${community ? '' : back('#/me')}
      <h1 tabindex="-1">${t('rules.title')}</h1>
      <ul class="rules" data-testid="rules">
        <li>${t('rules.join')}</li>
        <li>${t('rules.new', { items: items(param(w, 'newcomerOfferLimit')) })} ${t('rules.after', { n: param(w, 'offerLimit'), days: Math.round(param(w, 'rampAfter') / DAY) })}</li>
        <li>${t('rules.friction', { r: param(w, 'frictionReporters'), items: items(param(w, 'frictionOfferLimit')) })}</li>
        <li>${t('rules.stewards', { names })}</li>
        <li>${t('rules.vouchOnly')}</li>
        <li>${t('rules.caps', { v: param(w, 'vouchCap'), vd: Math.round(param(w, 'vouchWindow') / DAY), r: param(w, 'reportCap') })}</li>
        <li>${t('rules.appeal')}</li>
        <li>${t('rules.release')}</li>
        <li>${t('rules.ai')}</li>
        <li>${t('rules.rating')}</li>
        <li>${t('rules.linked')}</li>
        <li>${t('rules.forget')}</li>
        <li>${t('rules.change')}</li>
      </ul>
      ${community
        ? html`<h2>${t('rules.edit')}</h2>
          <form class="stack" data-form="policy">
            ${dial('newcomerOfferLimit', 'rules.f.new')}
            ${dial('offerLimit', 'rules.f.after')}
            ${dial('frictionReporters', 'rules.f.reporters')}
            ${dial('frictionOfferLimit', 'rules.f.friction')}
            <button class="btn" type="submit">${t('rules.publish')}</button>
          </form>`
        : ''}
    `,
  };
}

onForm('policy', async (fd, form) => {
  if (!form.reportValidity()) return;
  const next = JSON.parse(JSON.stringify(w.scope.policy)) as { params: Record<string, unknown> };
  for (const k of ['newcomerOfferLimit', 'offerLimit', 'frictionReporters', 'frictionOfferLimit']) next.params[k] = Number(fd.get(k));
  const { d } = await w.act(w.seat, { predicate: 'policy', body: next as never });
  if (d.admitted) flash(t('rules.published'));
  else refuse('policy', t('why.policy'), d);
  go('#/rules');
});

// Vouching: a member enters the code a newcomer shows them.
export function vouchScreen(): Screen {
  const l = local();
  const found = l.vouchFound ? w.byKey.get(l.vouchFound) : undefined;
  // Ask the gate about the voucher first (attested, under the cap); the subject is checked once a code is found.
  const gate = w.would(w.seat, { predicate: 'vouch', subject: found?.id ?? w.p('community').id });
  const failed = reason(gate)?.label;
  if (!gate.admitted && failed && !['vouch.not-self', 'vouch.first-for-subject'].includes(failed)) {
    const why = explain(w, 'vouch', gate, w.me.id);
    w.noteCheck(w.me.id, 'vouch', gate, why.summary);
    return { title: t('vouch.title'), tab: 'me', body: html`${back('#/me')}<h1 tabindex="-1">${t('vouch.title')}</h1>${whyCard(why, t('why.vouch'))}` };
  }
  return {
    title: t('vouch.title'),
    tab: 'me',
    body: html`
      ${back('#/me')}
      <h1 tabindex="-1">${t('vouch.title')}</h1>
      <form class="stack" data-form="vouch-find">
        <label class="field"><span>${t('vouch.code')}</span>
          <input name="code" required autocomplete="off" autocapitalize="characters" spellcheck="false" maxlength="7" class="code-input" value="${found ? codeOf(found.id) : ''}" /></label>
        ${l.error ? html`<p class="error" role="alert">${l.error}</p>` : ''}
        ${found ? '' : html`<button class="btn" type="submit">${t('vouch.find')}</button>`}
      </form>
      ${found
        ? html`<p class="lead">${t('vouch.found', { name: found.name[w.language], g: found.g })}</p>
          <p class="note">${t('vouch.warn')}</p>
          <button class="btn" type="button" data-act="vouch" data-id="${found.id}">${t('vouch.cta', { name: found.name[w.language] })}</button>`
        : ''}
    `,
  };
}

onForm('vouch-find', (fd) => {
  const code = String(fd.get('code')).trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  const l = local();
  const match = w.people.find((p) => codeOf(p.id).replace('-', '') === code && !isMember(w, p.id));
  l.vouchFound = match?.key;
  l.error = match ? undefined : t('vouch.notFound');
  go('#/vouch');
});

on('vouch', async (el) => {
  const id = el.dataset.id!;
  const l = local();
  const { d } = await w.act(w.seat, { predicate: 'vouch', subject: id });
  l.vouchFound = undefined;
  if (d.admitted) flash(t('vouch.done', { name: w.name(id), g: w.gender(id) }));
  else refuse('vouch', t('why.vouch'), d);
  go('#/vouch');
});
